import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { getLogos, readBrandingCache, writeBrandingCache } from '../utils/branding';

// 从 localStorage 读取缓存的品牌配置，避免页面切换时 logo 闪烁
// key 按站点 host 拆，见 utils/branding.js
const getCachedBranding = () => readBrandingCache();

const DEFAULT_BRANDING = {
  logos: [],
  logo: '',
  appName: 'Cinéart Canvas',
  version: 'Studio 1.0'
};

const CineartLogo = ({ onClick, style, compact = false }) => {
  const [branding, setBranding] = useState(() => {
    const cached = getCachedBranding();
    return cached ? { ...DEFAULT_BRANDING, ...cached } : DEFAULT_BRANDING;
  });
  // 🆕 加载失败的 logo（按 URL 记，多个 logo 各自独立失败）
  const [failedLogos, setFailedLogos] = useState([]);

  const logos = useMemo(() => getLogos(branding), [branding]);
  const logosKey = logos.join('|');

  // 配置变了就把失败标记清掉，让新 URL 有机会重试
  useEffect(() => { setFailedLogos([]); }, [logosKey]);

  const visibleLogos = logos.filter(url => !failedLogos.includes(url));
  const hasLogo = visibleLogos.length > 0;

  // 加载品牌配置并缓存
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const data = await api.get('/api/settings/login-branding');
        if (data) {
          setBranding(prev => ({ ...prev, ...data }));
          // 缓存到 localStorage，下次挂载时立即可用
          writeBrandingCache(data);
        }
      } catch (err) {
        console.warn('加载品牌配置失败，使用默认值:', err);
      }
    };
    loadBranding();
  }, []);

  return (
    <div
      onClick={onClick}
      style={{
        position: 'fixed',
        top: '24px',
        left: '24px',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        alignItems: compact ? 'center' : 'flex-start',
        pointerEvents: onClick ? 'auto' : 'none',
        userSelect: 'none',
        cursor: onClick ? 'pointer' : 'default',
        ...style
    }}>
      {/* 1. 图标部分 - 只有配置了 logo 且加载成功才显示；多个 logo 并排，间距 2px */}
      {hasLogo && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          {visibleLogos.map((url, index) => (
            <img
              key={`${url}-${index}`}
              src={url}
              alt=""
              onError={() => setFailedLogos(prev => prev.includes(url) ? prev : [...prev, url])}
              style={{
                // 写死 height：只给 max-* 时，无固有尺寸的 SVG（AI 导出常见，
                // 只有 viewBox 没有 width/height）在 flex 里会塌成 0×0
                height: compact ? '40px' : '48px',
                width: 'auto',
                maxWidth: compact ? '80px' : '96px',
                objectFit: 'contain'
                // 不要加 borderRadius：客户上传的方形 Logo 画面画到边，
                // 圆角会把四个角切掉，看起来像"缺角"。登录页那份也没有圆角，保持一致
              }}
            />
          ))}
        </div>
      )}

      {/* 2. 文字部分 —— compact 模式（如左侧拼屏时置于工具条上方）下隐藏，
              但若没有配置 logo 图标则保留软件名，避免完全空白 */}
      {(!compact || !hasLogo) && (
      <div style={{
        marginTop: hasLogo ? '8px' : '0',
        fontFamily: "'Inter', sans-serif",
        color: '#fff',
        letterSpacing: '0.5px',
        textAlign: compact ? 'center' : 'left'
      }}>
        {/* 软件名称 */}
        <div style={{
          fontSize: compact ? '11px' : '15px',
          fontWeight: '700',
          background: 'var(--logo-gradient)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          lineHeight: '1.3',
          whiteSpace: 'nowrap'
        }}>
          {branding.appName || 'Cinéart Canvas'}
        </div>
        {/* 版本号 —— compact 模式隐藏以保持紧凑 */}
        {!compact && <div style={{
          fontSize: '11px',
          fontWeight: '500',
          color: '#94a3b8',
          marginTop: '2px',
          lineHeight: '1.2',
          whiteSpace: 'nowrap'
        }}>
          {branding.version || 'Studio 1.0'}
        </div>}
      </div>
      )}
    </div>
  );
};

export default CineartLogo;
